import { Menu, Button, rem, useMantineTheme, useMantineColorScheme, Select, ActionIcon, Group, Switch } from '@mantine/core';
import { useThemeMode } from '../ThemeContext';
import { IconSettings, IconSun, IconMoon } from '@tabler/icons-react';
import React from 'react';
import i18n from '../locales/i18n';
import { changeLanguage } from '../locales/languageUtils';
import { useLanguages } from '../locales/languages';

interface LanguageSelectorProps {
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function LanguageSelector({ className = '', size = 'xs' }: LanguageSelectorProps) {
  const { languages, loading, error } = useLanguages();

  // languages is expected to be an array of { value: string, label: string }
  const currentLang = i18n.language;

  const { mode, setMode, toggleMode } = useThemeMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dropdownTextColor = colorScheme === 'dark' ? theme.white : theme.colors.dark[7] || '#222';

  return (
    <Menu shadow="md" width={180} position="bottom-end" styles={{
      dropdown: {
        maxHeight: 400,
        overflowY: 'auto',
        color: dropdownTextColor,
        backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
      },
    }}>
      <Menu.Target>
        <Button variant="subtle" px={6} py={6} style={{ minWidth: 0 }}>
          <IconSettings size={22} />
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Language</Menu.Label>
        <Select
          data={languages || []}
          value={languages?.some(l => l.value === currentLang) ? currentLang : 'en'}
          onChange={(val) => val && changeLanguage(val)}
          size={size}
          styles={{
            input: {
              color: dropdownTextColor,
              backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
              fontWeight: 'bold',
            },
            dropdown: {
              color: dropdownTextColor,
              backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
            },
          }}
        />
        <Menu.Divider />
        <Menu.Label>Theme</Menu.Label>
        <Group style={{ width: '100%', justifyContent: 'space-between', padding: 8 }}>
          <Switch
            checked={mode === 'dark'}
            onChange={(event) => {
              event.stopPropagation();
              toggleMode();
            }}
            size="md"
            color={mode === 'dark' ? 'yellow' : 'blue'}
            onLabel={<IconSun size={16} stroke={2.5} color="#FFD700" />} // sun icon
            offLabel={<IconMoon size={16} stroke={2.5} color="#22223b" />} // moon icon
            style={{ marginLeft: 8 }}
          />
        </Group>
      </Menu.Dropdown>
    </Menu>
  );
}
